import _ from "lodash";
import React from "react";
import * as I from "../Input";
import * as IT from "../../Item";
import * as H from "../../../hooks";
import * as S from "../../../services";
import { Modal as M } from "../../../Components";
import { FP, T, TB, TC } from "../../../Constants";

type Row = T.TicketData & Record<"_id", string>;
export type TicketsProps = Omit<I.InputProps, "options"> & Record<"options", T.Option<Record<"is_user", boolean>>[]>;

const Tickets: React.FC<TicketsProps> = ({ onChange, ...props }) => {
    const lg = H.useLanguage();
    const [forms] = H.useFormIds();
    const is_disabled = React.useMemo(() => props.disabled || props.noOverride, [props.disabled, props.noOverride]);

    const rows = React.useMemo<Row[]>(() => {
        // To always have at least one row displayed
        if (!Array.isArray(props.value) || props.value.length === 0) return [{ title: "", description: "", type: "maintenance" }];
        else return props.value;
    }, [props.value]);

    const options = React.useMemo(() => {
        const [users, ticket_types] = _.partition(props.options, o => o.is_user);
        return { ticket_types, users };
    }, [props.options]);

    const on_edit = React.useCallback((index: number) => {
        const row = rows[index];
        if (!row) return;

        const params = {
            noDbSaving: true,
            path: FP.TICKET_FORM,
            submissionId: row._id,
            forcedSubmission: TB.submissionToArrayUpdate({ ...row, _id: undefined }),
        } as Parameters<typeof M.renderFormModal<T.TicketData>>[0];

        M.renderFormModal(params).then(new_ticket => {
            if (new_ticket) {
                const new_rows = rows.map((ticket, i) => i === index ? { ...new_ticket.data, _id: row._id } : ticket);
                onChange?.(new_rows);
            }
        });
    }, [rows, onChange]);

    const on_delete = React.useCallback<IT.QuickInputProps2<Row>["onDelete"]>((index, row) => {
        // No need for confirmation for non-existing tickets
        if (!row._id) return true;
        else return new Promise<boolean>(resolve => {
            M.askConfirm({ title: TC.GLOBAL_DELETE, text: TC.TICKET_FORM_COMP_DELETE_ACTION }).then(confirmed => {
                if (confirmed) S.removeTickets({ ids: row._id })
                    .then(() => resolve(true))
                    .catch(() => resolve(false));
                else resolve(false);
            });
        });
    }, []);

    const columns = React.useMemo<IT.QuickInputProps2<Row>["columns"]>(() => [
        {
            size: 5,
            type: "text",
            textArea: true,
            asterisk: true,
            autoExpand: true,
            prop: "description",
            is_disabled: () => is_disabled,
            label: lg.getTextObj(forms[FP.TICKET_FORM], "description", "Description"),
        },
        {
            size: 3,
            prop: "type",
            type: "select",
            asterisk: true,
            options: options.ticket_types,
            init_value: "maintenance" as T.TicketData["type"],
            is_disabled: row => is_disabled || !!row.signedBy,
            label: lg.getTextObj(forms[FP.TICKET_FORM], "type", "Type"),
        },
        // {
        //     size: 3,
        //     type: "select",
        //     multiple: true,
        //     options: options.users,
        //     prop: "technicians" as keyof T.TicketData,
        //     is_disabled: row => is_disabled || !!row.signedBy,
        //     label: lg.getTextObj(forms[FP.TICKET_FORM], "technicians", "Technicians"),
        // },
        {
            size: 3,
            prop: "free_render",
            type: "free_render",
            label: TC.GLOBAL_STATUS,
            className: "text-center",
            render: (row: Row) => {
                // Ticket is signed and closed
                if (row?.signedBy) return lg.getStaticText(TC.TICKET_CLOSED);
                // Ticket as a date of start or a date of end
                else if (row?.start || row?.end) return lg.getStaticText(TC.TICKET_ATTRIBUTED);
                // Ticket is open
                else return lg.getStaticText(TC.TICKET_OPEN);
            },
        },
        {
            size: 1,
            label: "",
            prop: "edit",
            type: "button",
            onClick: on_edit,
            className: "w-100",
            icon: "pencil-alt",
            variant: "primary",
            is_disabled: () => is_disabled,
        },
    ], [lg, forms, on_edit, props.options, is_disabled]);

    return <I.ComponentWrapper {...props} disabled={is_disabled}>
        <IT.QuickInput2
            controlled
            button_top
            rows={rows}
            no_init_focus
            columns={columns}
            onDelete={on_delete}
            gel_rows={is_disabled}
            onSubmit={v => onChange?.(v)}
        />
    </I.ComponentWrapper>;
}

export default Tickets;